import React from "react";
import styled from "@emotion/styled";
import { useRouteMatch } from "react-router";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import { Grid } from "../../UI/Grid";
import { Typography } from "../../UI/Typography";
import {
  useNetworkContext,
  useSelectedNetworkContext,
} from "../../contexts/NetworkContext/context";
import { useNetworksContext } from "../../contexts/NetworksProvider";
import useAuth from "../../routes/login/AuthContext";
import { NoNetworksNotice } from "../../components/NoNetworksNotice";
import { Skeleton } from "../../UI/Skeleton";
import { URL } from "../../constants/navigation";
import { COLOR_FIORD } from "../../constants/styles";
import { STICKY_HEADER_STYLES } from "../../common/styles";
import { HelpTooltip } from "../../components/HelpTooltip";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { PORTFOLIO_LIST } from "../../routes/super_admin/portfolio/PortfolioList";
import { PORTFOLIO_DETAILED } from "../../routes/super_admin/portfolio/PortfolioDetailedView";
import { PROFILE_PHOTO_JOBS } from "../../routes/super_admin/profile_photos/ProfilePhotoJobs";

const StyledMain = styled.main<{ isUpMd: boolean }>`
  height: 100%;
  overflow-y: scroll;
  position: relative;
`;

interface IProps {
  title?: string;
  subtitle?: string;
  titleHelpText?: string;
  endAdornment?: React.ReactNode;
  subEndAdornment?: React.ReactNode;
  stickyHeader?: boolean;
  headerMt?: number;
}

const SUPER_ADMIN_PAGES_WITHOUT_NETWORK = [
  URL.ADMIN_ACCESS_REQUEST_CONTROL,
  URL.PUSH_TEST,
  URL.ENTRATA,
  URL.NETWORK_SETTINGS,
  PORTFOLIO_LIST,
  PORTFOLIO_DETAILED,
  PROFILE_PHOTO_JOBS,
];

export const StyledHeaderWrapper = styled(Grid)`
  height: 76px;
  border-bottom: ${(props) => `1px solid ${props.theme.palette.divider}`};
`;

export const MainLayout: React.FC<IProps> = (props) => {
  const { isLoading } = useNetworkContext();
  const { networks, isLoading: isLoadingNetworks } = useNetworksContext();
  const { isSuperAdmin } = useAuth();
  const { isUpMd } = useMediaQuery();
  const superAdminPage = useRouteMatch(SUPER_ADMIN_PAGES_WITHOUT_NETWORK);
  const userPage = useRouteMatch(URL.USER);
  const { selectedNetworkId } = useSelectedNetworkContext();

  // An enabled admin who hasn't been granted any networks yet.
  const hasNoNetworks =
    !isSuperAdmin() && !isLoadingNetworks && networks.length === 0;

  const isDefaultNetwork = selectedNetworkId === 0;
  const isContent =
    superAdminPage || userPage || (!isLoading && !isDefaultNetwork);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <StyledMain isUpMd={isUpMd}>
        <Grid container pb={2}>
          {props.title && (
            <StyledHeaderWrapper
              item
              container
              alignItems="center"
              mt={props.headerMt}
              px={4}
              mb={4}
              sx={props.stickyHeader ? STICKY_HEADER_STYLES.header : {}}
            >
              <Grid item xs>
                {props.titleHelpText && (
                  <HelpTooltip title={props.title} text={props.titleHelpText}>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: "500 !important" }}
                    >
                      {props.title}
                    </Typography>
                  </HelpTooltip>
                )}
                {!props.titleHelpText && (
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: "500 !important" }}
                  >
                    {props.title}
                  </Typography>
                )}
              </Grid>
              {props.endAdornment && <Grid item>{props.endAdornment}</Grid>}
            </StyledHeaderWrapper>
          )}
          <Grid container alignItems="stretch" justifyContent="space-between">
          {props.subtitle ?
            (
              <Grid item>
                <Typography
                  variant="h6"
                  ml={5}
                  mb={2}
                  sx={{ fontWeight: "500 !important" }}
                >
                  {props.subtitle}
                </Typography>
           </Grid>
            ) : null
          }
          {props.subEndAdornment && <Grid item>{props.subEndAdornment}</Grid>}
          </Grid>
          {!props.title && <Grid item height={32} xs={12} />}
          {isLoading &&
            !userPage && ( // We need to load user page components to get user l1id and then made a request for the network
              <Grid item xs={12} px={4}>
                <Skeleton
                  variant="text"
                  width="100%"
                  height={100}
                  sx={{ mb: 2 }}
                />
                <Skeleton variant="rectangular" width="100%" height={400} />
              </Grid>
            )}
          {!isContent && !isLoading && (
            <Grid item xs={12} px={4}>
              {hasNoNetworks ? (
                <NoNetworksNotice />
              ) : (
                <Typography variant="h6" color={COLOR_FIORD}>
                  Please select the network
                </Typography>
              )}
            </Grid>
          )}
          {isContent && (
            <Grid item xs={12} px={4} pb={6}>
              {props.children}
            </Grid>
          )}
        </Grid>
      </StyledMain>
    </LocalizationProvider>
  );
};